class SideSearch extends HTMLElement {
    constructor() {
        super();

        // bindings
        this.onOverlayClick = this.onOverlayClick.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onInput = this.onInput.bind(this);

        // state
        this.isLoading = false;
        this.searchDebounceTimer = null;
        this.abortController = null;
        this.lastQuery = "";
    }

    connectedCallback() {
        this.overlayEl = this.querySelector(".side-search__overlay");
        this.closeEl = this.querySelector("[data-sidesearch-close]");
        this.inputEl = this.querySelector("#side-search__input");
        this.resultEl = this.querySelector("#search-result");
        this.moreProductsEl = this.querySelector("#more-products");

        this.overlayEl?.addEventListener("click", this.onOverlayClick);
        this.closeEl?.addEventListener("click", this.onCloseClick);
        document.addEventListener("keydown", this.onKeydown);

        // Recherche live (debounce)
        this.inputEl?.addEventListener("input", this.onInput);
    }

    disconnectedCallback() {
        this.overlayEl?.removeEventListener("click", this.onOverlayClick);
        this.closeEl?.removeEventListener("click", this.onCloseClick);
        document.removeEventListener("keydown", this.onKeydown);

        this.inputEl?.removeEventListener("input", this.onInput);

        // cleanup timers / fetch
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
        if (this.abortController) this.abortController.abort();
    }

    open() {
        this.classList.add("is-active");
        document.body.style.overflow = "hidden";
        setTimeout(() => this.inputEl?.focus(), 0);
    }

    close() {
        this.classList.remove("is-active");
        document.body.style.overflow = "";

        // optionnel: annule une recherche en cours quand on ferme
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
        if (this.abortController) this.abortController.abort();
        this.isLoading = false;
    }

    onOverlayClick() {
        this.close();
    }

    onCloseClick() {
        this.close();
    }

    onKeydown(e) {
        if (e.key === "Escape" && this.classList.contains("is-active")) {
            this.close();
        }
    }

    onInput() {
        const q = (this.inputEl?.value || "").trim();

        // reset debounce timer à chaque frappe
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);

        // Si moins de 3 caractères : on annule requête + on nettoie l'affichage
        if (q.length < 3) {
            this.lastQuery = q;

            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }

            this.isLoading = false;

            if (this.resultEl) this.resultEl.innerHTML = "";
            if (this.moreProductsEl) this.moreProductsEl.classList.add("hide");
            return;
        }

        // Evite de relancer si même requête (ex: espaces)
        if (q === this.lastQuery) return;

        // Debounce 700ms
        this.searchDebounceTimer = setTimeout(() => {
            this.runSearch(q);
        }, 700);
    }

    async runSearch(q) {
        // Annule le fetch précédent s'il existe
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        this.isLoading = true;
        this.lastQuery = q;

        if (this.resultEl) {
            this.resultEl.innerHTML = `<div class="search-result__loading">Recherche…</div>`;
        }
        if (this.moreProductsEl) {
            this.moreProductsEl.classList.add("hide");
        }

        try {
            const url = `/search?sections=main-search&q=${encodeURIComponent(q)}&type=product,article`;

            const res = await fetch(url, {
                headers: { Accept: "application/json" },
                signal: this.abortController.signal,
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const data = await res.json();
            const html = data["main-search"];

            // Vérifie s'il y a au moins un produit
            let hasProduct = false;
            if (html) {
                const doc = new DOMParser().parseFromString(html, "text/html");
                hasProduct = doc.querySelector(".search__product") !== null;
            }

            // Si l'utilisateur a déjà tapé autre chose entre temps, on ignore ce résultat
            if (q !== this.lastQuery) return;

            if (this.resultEl) {
                this.resultEl.innerHTML = hasProduct
                    ? html
                    : `<div class="search-result__empty">Aucun résultat.</div>`;
            }
            if (!hasProduct) {
                if (this.moreProductsEl) {
                    this.moreProductsEl.classList.remove("hide");
                }
            }
        } catch (err) {
            // Abort = normal (l'utilisateur continue de taper)
            if (err?.name === "AbortError") return;

            console.error(err);

            // Pareil: on n'affiche pas une erreur si la query a changé
            if (q !== this.lastQuery) return;

            if (this.resultEl) {
                this.resultEl.innerHTML = `<div class="search-result__error">Erreur de recherche.</div>`;
            }
        } finally {
            if (q === this.lastQuery) {
                this.isLoading = false;
            }
        }
    }
}

// Evite l'erreur si le script est chargé 2 fois
if (!customElements.get("side-search")) {
    customElements.define("side-search", SideSearch);
}

/* Open triggers: data-sidesearch-open="custom-search" */
document.addEventListener("click", (e) => {
    const trigger = e.target.closest("[data-sidesearch-open]");
    if (!trigger) return;

    const id = trigger.getAttribute("data-sidesearch-open");
    const el = document.getElementById(id);

    if (el?.tagName?.toLowerCase() !== "side-search") return;
    el.open?.();
});

/* Close triggers: data-sidesearch-close="custom-search" */
document.addEventListener("click", (e) => {
    const trigger = e.target.closest("[data-sidesearch-close]");
    if (!trigger) return;

    const id = trigger.getAttribute("data-sidesearch-close");
    const el = document.getElementById(id);

    if (el?.tagName?.toLowerCase() !== "side-search") return;
    el.close?.();
});
